import { WorkOrder, WorkOrderItem, WorkOrderSection } from '../models/work-order.model';

/** A work order's sections in the order the work order lists them. */
export function orderedWorkOrderSections(workOrder: WorkOrder | null | undefined): WorkOrderSection[] {
    return [...(workOrder?.sections ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/** A section's items in the order the section lists them. */
export function orderedSectionItems(section: WorkOrderSection | null | undefined): WorkOrderItem[] {
    return [...(section?.items ?? [])].sort((a, b) => a.position - b.position);
}

/**
 * Every item of a work order, its sections in order, so counts cover the whole work order the way the
 * technician sees it, jobs included.
 */
export function allWorkOrderItems(workOrder: WorkOrder | null | undefined): WorkOrderItem[] {
    return orderedWorkOrderSections(workOrder).flatMap((section) => orderedSectionItems(section));
}

/** Whether an item is a remark: a note with no part or labour line under it. */
export function isRemarkItem(item: WorkOrderItem): boolean {
    return !item.lines?.length;
}

/** Remarks: items a person wrote, with no part or labour line. */
export function countRemarks(items: readonly WorkOrderItem[]): number {
    return items.filter((item) => isRemarkItem(item)).length;
}

/**
 * Sale lines (parts and labour), one per sale item. A package is one item whose lines are its parts and labour, so it
 * counts one per line, as each of them was counted when every package line was an item of its own.
 */
export function countSaleLines(items: readonly WorkOrderItem[]): number {
    return items
        .filter((item) => !isRemarkItem(item))
        .reduce((count, item) => count + (item.packageId ? (item.lines?.length ?? 0) : 1), 0);
}

/** A remark's text split into the technician prefix Raptor writes and what the technician typed. */
export interface RemarkAuthorship {
    /** The technician's name as the prefix carries it. */
    author: string;
    /** The branch-local time in the prefix, `HH:mm`. */
    time: string;
    /** The remark without the prefix. */
    body: string;
}

/**
 * The prefix as Raptor writes it: a person's name of one to four capitalised words, then the time. The name has to
 * read as a name so a remark typed in the system that happens to hold a time, such as
 * `Called customer 10:15: no answer`, is not taken for a technician's byline.
 */
const REMARK_PREFIX = /^(\p{Lu}[\p{L}'.-]*(?: \p{Lu}[\p{L}'.-]*){0,3}) ((?:[01]\d|2[0-3]):[0-5]\d): ([\s\S]*)$/u;

/**
 * Reads the `<Name> HH:mm: ` prefix Raptor puts in front of a technician's remark, so the app can show
 * the name and time under the remark instead of inside it. Display only: the stored text is never
 * changed. Text without that exact prefix (a remark typed in the system, or the continuation lines of a long
 * remark) returns null and is shown as it is.
 */
export function parseRemarkAuthorship(text: string | null | undefined): RemarkAuthorship | null {
    if (!text) {
        return null;
    }
    const match = REMARK_PREFIX.exec(text);
    if (!match) {
        return null;
    }
    const author = match[1].trim();
    const body = match[3];
    return author.length <= 60 && body.trim().length > 0 ? { author, time: match[2], body } : null;
}
